import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "user-input-container" }
const _hoisted_2 = {
  key: 0,
  class: "selected-text"
}
const _hoisted_3 = { class: "display" }
const _hoisted_4 = { class: "body s bold flex-0" }
const _hoisted_5 = { class: "body s flex-1" }
const _hoisted_6 = { class: "user-input-area" }
const _hoisted_7 = { class: "body s font-regular text-align-left" }
const _hoisted_8 = ["placeholder", "disabled"]
const _hoisted_9 = { class: "action-icons" }

import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import CustomCheckbox from '@/components/common/checkbox/CustomCheckbox.vue';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import { useToasterStore } from '@/stores/useToasterStore';
import { ChatUtils } from '@/utils/chat.utils';
import { OfficeUtils } from '@/utils/office.utils';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
  watchEffect
} from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  value?: string;
  placeholder?: string;
  onSend: (text: string) => Promise<void> | void;
  disabled?: boolean;
  allowDocumentTextSelection?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatInputComponent',
  props: {
    value: {},
    placeholder: {},
    onSend: {},
    disabled: { type: Boolean, default: false },
    allowDocumentTextSelection: { type: Boolean, default: false }
  },
  emits: ['update:value'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

onMounted(() => {
  if (props.value) {
    internalValue.value = props.value;
  }
});

const emit = __emit;
const useDocumentSelectedText = ref<boolean>(false);
const documentSelectedText = ref<string | undefined>();

const { t } = useI18n();
const toaster = useToasterStore();
const internalValue = ref(props.value || '');
const loading = ref(false);
const selecting = ref(false);

const isDisabled = computed(
  () => loading.value || props.disabled || selecting.value
);

const showTextSelectionToggle = computed(
  () => props.allowDocumentTextSelection && !OfficeUtils.isOutlook()
);

if (props.allowDocumentTextSelection) {
  onMounted(() => {
    OfficeUtils.startDocumentSelectionListener(selectionChangedHandler);
  });

  onUnmounted(() => {
    OfficeUtils.stopDocumentSelectionListener(selectionChangedHandler);
  });

  watchEffect(() => {
    if (useDocumentSelectedText.value) {
      selectionChangedHandler();
    }
  });
}

async function selectionChangedHandler(): Promise<void> {
  if (useDocumentSelectedText.value) {
    try {
      selecting.value = true;
      let text = await OfficeUtils.getSelectedText();
      if (!text) return;
      const words = text.split(' ');

      if (words.length > 5) {
        toaster.warning({
          title: t('legal_assistant.common.selected_text_warning_title'),
          body: t('legal_assistant.common.selected_text_warning_body')
        });

        text = words.slice(0, 5).join(' ') + '...';
      }

      documentSelectedText.value = text;
    } catch (error) {
      console.error('Error updating selected text:', error);
    } finally {
      selecting.value = false;
    }
  }
}

async function sendText() {
  if (isDisabled.value || !internalValue.value.trim()) return;
  loading.value = true;
  try {
    let valueToSend = internalValue.value.trim();

    if (props.allowDocumentTextSelection && useDocumentSelectedText.value) {
      valueToSend = ChatUtils.formatSelectedTextAndMessage(
        documentSelectedText.value || '',
        valueToSend
      );
    }

    clearText();
    await props.onSend(valueToSend);
  } catch (error) {
    if (error instanceof ErrorWithToastData)
      toaster.error({ title: error.title, body: error.body });

    console.error('Error sending text:', error);
  } finally {
    loading.value = false;
  }
}

function clearText() {
  internalValue.value = '';
  clearDocumentSelectedText();
  adjustHeight(true);
}

function clearDocumentSelectedText(): void {
  documentSelectedText.value = undefined;
  useDocumentSelectedText.value = false;
  OfficeUtils.clearTextSelection();
}

function toggleUseSelectedText() {
  if (loading.value || props.disabled) return;
  useDocumentSelectedText.value = !useDocumentSelectedText.value;
}

const textarea = ref<HTMLTextAreaElement | null>(null);

function adjustHeight(resetToMin = false) {
  if (!textarea.value) return;
  const cs = window.getComputedStyle(textarea.value);
  if (resetToMin) {
    textarea.value.style.height =
      cs.minHeight !== '0px' ? cs.minHeight : 'auto';
    textarea.value.style.overflowY = 'hidden';
  } else {
    textarea.value.style.height = 'auto';
    const sh = textarea.value.scrollHeight;
    const maxH = parseInt(cs.maxHeight, 10);
    textarea.value.style.height = `${Math.min(sh, maxH)}px`;
    textarea.value.style.overflowY = sh > maxH ? 'auto' : 'hidden';
  }
}

watch(
  () => props.value,
  (newVal) => {
    internalValue.value = newVal || '';
  }
);

watch(internalValue, (newVal) => {
  emit('update:value', newVal);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.allowDocumentTextSelection && documentSelectedText.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(AppIcon, {
              name: "copy-text",
              "class-name": "flex-0"
            }),
            _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('legal_assistant.common.selected_text')), 1),
            _createElementVNode("p", _hoisted_5, _toDisplayString(documentSelectedText.value), 1),
            _createElementVNode("div", {
              role: "button",
              class: "flex-0 display-flex align-center",
              onClick: clearDocumentSelectedText
            }, [
              _createVNode(AppIcon, {
                name: "X",
                "class-name": "clickable"
              })
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      (showTextSelectionToggle.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "use-selected-text-wrapper",
            onClick: toggleUseSelectedText
          }, [
            _createVNode(CustomCheckbox, {
              modelValue: useDocumentSelectedText.value,
              disabled: isDisabled.value
            }, null, 8, ["modelValue", "disabled"]),
            _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)(
              `legal_assistant.actions.use_selected_text${
                _unref(OfficeUtils).isOutlook() ? '.outlook' : ''
              }`
            )), 1)
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("textarea", {
        ref_key: "textarea",
        ref: textarea,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((internalValue).value = $event)),
        placeholder: _ctx.placeholder ?? _ctx.$t('legal_assistant.textarea.placeholder'),
        disabled: isDisabled.value,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (adjustHeight())),
        onKeydown: _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (sendText()), ["prevent"]), ["enter"]))
      }, null, 40, _hoisted_8), [
        [_vModelText, internalValue.value]
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createVNode(AppIcon, {
          name: "send",
          size: "body-xl",
          "class-name": "color-lighter",
          onclick: sendText,
          clickable: !isDisabled.value
        }, null, 8, ["clickable"])
      ])
    ])
  ]))
}
}

})